import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  PlayCircleIcon,
  MegaphoneIcon,
  PencilSquareIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ArrowRightIcon,
  GlobeAltIcon,
  CalendarDaysIcon,
  PhotoIcon,
  VideoCameraIcon,
  ScissorsIcon,
  SwatchIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import './styles.css';

const features = [
  {
    title: 'Criação de conteúdo com IA',
    description: 'Gere posts, imagens, carrosséis e vídeos em minutos com base no seu objetivo.',
  },
  {
    title: 'Identidade visual da marca',
    description: 'Crie peças alinhadas ao seu posicionamento e consistência visual.',
  },
  {
    title: 'Gestão de redes sociais',
    description: 'Planeje conteúdos para Instagram, Facebook, LinkedIn e campanhas.',
  },
];

const steps = [
  { title: 'Crie', description: 'Digite o que deseja divulgar e escolha o formato ideal.', icon: PencilSquareIcon },
  { title: 'Personalize', description: 'Ajuste imagens, textos e identidade da sua marca.', icon: SparklesIcon },
  { title: 'Publique', description: 'Escolha onde e quando publicar com poucos cliques.', icon: MegaphoneIcon },
  { title: 'Analise', description: 'Acompanhe resultados e otimize sua estratégia.', icon: ChartBarIcon },
];

const workflowItems = [
  {
    title: 'Integração com canais e operação centralizada',
    description: 'Organize sua criação em um fluxo único para redes sociais, campanhas e conteúdos de marca.',
    image: '/IMG/Gemini_Generated_Image_a1bwzja1bwzja1bw.png',
    icon: GlobeAltIcon,
  },
  {
    title: 'Programação e publicação com consistência',
    description: 'Planeje conteúdos com antecedência e mantenha frequência de postagem com mais previsibilidade.',
    image: '/IMG/Gemini_Generated_Image_gjqyc3gjqyc3gjqy.png',
    icon: CalendarDaysIcon,
  },
  {
    title: 'Criação de peças visuais em escala',
    description: 'Gere variações de criativos para ofertas, campanhas e lançamentos em poucos minutos.',
    image: '/IMG/Gemini_Generated_Image_pt6d1wpt6d1wpt6d.png',
    icon: PhotoIcon,
  },
  {
    title: 'Mensuração com foco em resultado',
    description: 'Acompanhe o desempenho para otimizar sua estratégia criativa e decisões de negócio.',
    image: '/IMG/Gemini_Generated_Image_yy9q5hyy9q5hyy9q.png',
    icon: ChartBarIcon,
  },
];

const smartTools = [
  { title: 'Editor de vídeo', icon: VideoCameraIcon },
  { title: 'Editor de imagens', icon: PhotoIcon },
  { title: 'Corte inteligente', icon: ScissorsIcon },
  { title: 'Layouts de design', icon: SwatchIcon },
  { title: 'Carrosséis prontos', icon: Squares2X2Icon },
  { title: 'Planejamento de campanhas', icon: CalendarDaysIcon },
];

const faqs = [
  {
    question: 'Como funciona o sistema sem mensalidade?',
    answer: 'Você usa créditos conforme a demanda. Sem cobrança fixa mensal.',
  },
  {
    question: 'Preciso ter conhecimento técnico?',
    answer: 'Não. O fluxo foi feito para ser simples e guiado, mesmo para iniciantes.',
  },
  {
    question: 'Os créditos têm validade?',
    answer: 'Não. Você pode usar no seu ritmo, quando quiser.',
  },
  {
    question: 'Tem suporte em português?',
    answer: 'Sim. Atendimento em português para dúvidas de uso e configuração.',
  },
];

function SectionTitle({ icon: Icon, label, title, subtitle }) {
  return (
    <header className="landing-section__header">
      <span className="landing-section__label">
        <Icon />
        {label}
      </span>
      <h2>{title}</h2>
      {subtitle ? <p>{subtitle}</p> : null}
    </header>
  );
}

export function LandingTopMenu() {
  return (
    <nav className="landing-nav">
      <div className="landing-container landing-nav__inner">
        <Link to="/free" className="landing-brand">
          <div className="landing-hero__brand-icon">M</div>
          <span className="landing-hero__brand-name">Marketing Pro</span>
        </Link>

        <div className="landing-nav__steps">
          <span className="landing-nav__step landing-nav__step--done">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
            Criação
          </span>
          <span className="landing-nav__step landing-nav__step--active">Refino</span>
          <span className="landing-nav__step">Vendas</span>
        </div>

        <div className="landing-nav__actions">
          <Link to="/login" className="ui-btn ui-btn--ghost">Entrar</Link>
          <Link to="/free" className="ui-btn ui-btn--primary">Começar grátis</Link>
        </div>
      </div>
    </nav>
  );
}

export function LandingSections() {
  const [openFaq, setOpenFaq] = useState(0);
  const faqItems = useMemo(() => faqs, []);

  return (
    <>
      <section className="landing-hero">
        <div className="landing-container landing-hero__grid">
          <div className="landing-hero__content">
            <div className="landing-hero__brand">
              <div className="landing-hero__brand-icon">M</div>
              <span className="landing-hero__brand-name">Marketing Pro</span>
            </div>
            <p className="landing-hero__tagline">Sua Agência de Marketing na Palma da sua Mão</p>
            <h1>Marketing Pro: Aumente Suas Vendas Online!</h1>
            <p>
              Sua app para criar campanhas que vendem de forma fácil com a inteligência do Edy
            </p>
            <div className="landing-hero__input-wrap">
              <input
                type="text"
                className="landing-hero__input"
                placeholder="Qual nome de sua empresa?"
                readOnly
                onClick={() => window.location.href = '/free'}
              />
              <Link to="/free" className="ui-btn ui-btn--primary" style={{ width: 'fit-content' }}>Próximo</Link>
            </div>
          </div>

          <div className="landing-hero__visual">
            <div className="landing-hero__chat-bubble">
              Oi! Prontos para aumentar suas vendas?
            </div>
            <img src="/robot.png" alt="Eddy - assistente de IA" className="landing-hero__robot" />
          </div>
        </div>
      </section>

      <section className="landing-strip">
        <div className="landing-container landing-strip__grid">
          <article className="card landing-strip__item">
            <strong>+300%</strong>
            <span>Potencial de ganho em consistência de conteúdo</span>
          </article>
          <article className="card landing-strip__item">
            <strong>Até 95%</strong>
            <span>Redução de custo operacional de criação</span>
          </article>
          <article className="card landing-strip__item">
            <strong>Fluxo ponta a ponta</strong>
            <span>Criação, edição, publicação e análise no mesmo lugar</span>
          </article>
        </div>
      </section>

      <section className="landing-section" id="funcionalidades">
        <div className="landing-container">
          <SectionTitle
            icon={SparklesIcon}
            label="Como Funciona?"
            title="Tudo que você precisa para vender mais"
            subtitle="Ferramentas para criar, publicar e otimizar sua comunicação em um único fluxo."
          />

          <div className="landing-grid landing-grid--3">
            {features.map((feature) => (
              <article key={feature.title} className="landing-card card">
                <div className="landing-card__icon">
                  <img src="/robot.png" alt="" style={{ width: 40, height: 40, objectFit: 'contain' }} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" id="como-funciona">
        <div className="landing-container">
          <SectionTitle
            icon={PlayCircleIcon}
            label="Funcionalidades Powered"
            title="Simples, rápido e eficiente"
            subtitle="Em 4 passos você cria e publica campanhas com qualidade profissional."
          />

          <div className="landing-grid landing-grid--4">
            {steps.map((step, index) => (
              <article key={step.title} className="card landing-step">
                <span className="landing-step__number">{String(index + 1).padStart(2, '0')}</span>
                <step.icon className="landing-step__icon" />
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" id="fluxo">
        <div className="landing-container">
          <SectionTitle
            icon={PlayCircleIcon}
            label="Fluxo contínuo"
            title="Criação de conteúdo de ponta a ponta"
            subtitle="Uma estrutura visual mais próxima de produto SaaS: blocos grandes, foco em fluxo e CTA contextual."
          />

          <div className="landing-workflow">
            {workflowItems.map((item) => (
              <article key={item.title} className="card landing-workflow__card">
                <div className="landing-workflow__media">
                  <img src={item.image} alt={item.title} />
                </div>
                <div className="landing-workflow__content">
                  <div className="landing-workflow__icon"><item.icon /></div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <Link to="/free" className="landing-workflow__link">
                    Experimentar agora
                    <ArrowRightIcon />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" id="ferramentas">
        <div className="landing-container">
          <SectionTitle
            icon={SparklesIcon}
            label="Ferramentas"
            title="Tudo que você precisa para escalar conteúdo"
            subtitle="Blocos enxutos e organizados, com leitura rápida e estrutura clara."
          />

          <div className="landing-tools">
            {smartTools.map((tool) => (
              <article key={tool.title} className="card landing-tools__item">
                <tool.icon />
                <h3>{tool.title}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" id="faq">
        <div className="landing-container">
          <SectionTitle
            icon={ChevronDownIcon}
            label="FAQ"
            title="Perguntas frequentes"
            subtitle="Principais dúvidas sobre uso, cobrança e suporte."
          />

          <div className="landing-faq card">
            {faqItems.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <button
                  key={faq.question}
                  type="button"
                  className={`landing-faq__item ${isOpen ? 'is-open' : ''}`}
                  onClick={() => setOpenFaq(isOpen ? -1 : index)}
                >
                  <div className="landing-faq__question">
                    <h3>{faq.question}</h3>
                    <ChevronDownIcon />
                  </div>
                  {isOpen ? <p>{faq.answer}</p> : null}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="landing-cta-section landing-section">
        <div className="landing-container" style={{ textAlign: 'center', border: 'none', background: 'transparent', backdropFilter: 'none' }}>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 800, color: '#fff', marginBottom: 16, textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Sua Jornada para Vender HOJE
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.65)', maxWidth: 480, margin: '0 auto 28px', fontSize: '1rem', lineHeight: 1.6 }}>
            Comece agora e transforme sua presença digital com campanhas que convertem
          </p>
          <Link to="/free" className="ui-btn ui-btn--primary" style={{ fontSize: '1.1rem', padding: '16px 36px' }}>
            Começar Agora
          </Link>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-container landing-footer__inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="landing-hero__brand-icon" style={{ width: 32, height: 32, fontSize: '1rem', borderRadius: 10 }}>M</div>
            <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>Marketing Pro</span>
          </div>
          <div className="landing-footer__links" style={{ display: 'flex', gap: 24 }}>
            <Link to="/about" style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Sobre Nós</Link>
            <Link to="/terms" style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Termos</Link>
            <Link to="/privacy" style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Política de Uso</Link>
          </div>
        </div>
      </footer>
    </>
  );
}
